import { GRID_CHECKBOX_SELECTION_COL_DEF, GridCellParams, GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { DispatchColumnDefinitions, DispatchLegColumns } from './types';
import { BoardQuery } from '@api/Trinium/types';
import RowActions from './Cells/RowActions';
import { getObjectEntryByKey } from '@/Infrastructure/Utilities/object.type';
import { getEnumEntryByString } from '@/Infrastructure/Utilities/enum.type';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import RowSelectCheckbox from './Cells/RowSelectCheckbox';
import classNames from 'classnames/bind';
import orderNumberDefinition from './ColumnDefinitions/orderNumber';
import legDefinition from './ColumnDefinitions/leg';
import containerDefinition from './ColumnDefinitions/container';
import containerProviderDefinition from './ColumnDefinitions/containerProvider';
import driverDefinition from './ColumnDefinitions/driver';
import chassisDefinition from './ColumnDefinitions/chassis';
import pickupDefinition from './ColumnDefinitions/pickup';
import sealNumberDefinition from './ColumnDefinitions/sealNumber';
import dehireDefinition from './ColumnDefinitions/dehire';
import deliverDefinition from './ColumnDefinitions/deliver';
import deliverDateDefinition from './ColumnDefinitions/deliverDate';
import dropDateDefinition from './ColumnDefinitions/dropDate';
import customerDefinition from './ColumnDefinitions/customer';
import vesselDefinition from './ColumnDefinitions/vessel';
import scheduledPickupDefinition from './ColumnDefinitions/scheduledPickup';
import scheduledDeliveryDefinition from './ColumnDefinitions/scheduledDelivery';
import notesDefinition from './ColumnDefinitions/notes';
import consignmentNotesDefinition from './ColumnDefinitions/consignmentNotes';
import bookingBolDefinition from './ColumnDefinitions/booking';
import releaseNumberDefinition from './ColumnDefinitions/releaseNumber';
import pickupTimeDefinition from './ColumnDefinitions/pickupTime';
import availabilityDefinition from './ColumnDefinitions/availability';
import jobDateDefinition from './ColumnDefinitions/jobDate';
import lastFreeDayDefinition from './ColumnDefinitions/lastFreeDate';
import railBillingDefinition from './ColumnDefinitions/railBilling';
import traceContainerAvailabilityDefinition from './ColumnDefinitions/traceContainerAvailability';
import traceContainerHoldsDefinition from './ColumnDefinitions/traceContainerHolds';
import traceContainerStatusDefinition from './ColumnDefinitions/traceContainerStatus';
import traceDestinationInfoDefinition from './ColumnDefinitions/traceDestinationInfo';
import traceEventDefinition from './ColumnDefinitions/traceEvent';
import traceInvoiceStatusDefinition from './ColumnDefinitions/traceInvoiceStatus';
import traceLastFreeDateDefinition from './ColumnDefinitions/traceLastFreeDate';
import traceResultDefinition from './ColumnDefinitions/traceResult';
import traceEtaDateDefinition from './ColumnDefinitions/traceEtaDate';
import traceEventStateDefinition from './ColumnDefinitions/traceEventState';

import styles from '../RenderCell.module.css';
const stylesCtx = classNames.bind(styles);

export const defaultPinnedColumns: GridPinnedColumnFields = {
  left: ['__check__', 'actionMenu', 'orderNumber', 'Driver'],
  right: []
};

/**
 * Column Definitions for the Dispatch Leg model for use with the Data Grid
 */
export const dispatchColumnDefinitions: DispatchColumnDefinitions = {
  actionMenu: {
    field: 'actionMenu',
    minWidth: 1,
    width: 20,
    renderHeader: () => <></>,
    renderCell: ({ row }) => <RowActions row={row} />,
    resizable: false,
    sortable: false,
    disableReorder: true,
    disableExport: true,
    /** Grid furniture rather than data, so it is not offered in the Show/Hide Columns panel */
    hideable: false
  },
  __check__: {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    renderCell: RowSelectCheckbox,
    width: 60,
    resizable: false,
    disableExport: true,
    /** Grid furniture rather than data, so it is not offered in the Show/Hide Columns panel */
    hideable: false
  },
  orderNumber: orderNumberDefinition,
  leg: legDefinition,
  container: containerDefinition,
  containerProvider: containerProviderDefinition,
  chassis: chassisDefinition,
  driver: driverDefinition,
  pickup: pickupDefinition,
  puSealNo: sealNumberDefinition,
  dehire: dehireDefinition,
  deliver: deliverDefinition,
  deliverDate: deliverDateDefinition,
  dropDate: dropDateDefinition,
  customer: customerDefinition,
  vessel: vesselDefinition,
  notes: notesDefinition,
  csnNotes: consignmentNotesDefinition,
  railBilling: railBillingDefinition,
  scheduledPickup: scheduledPickupDefinition,
  scheduledDelivery: scheduledDeliveryDefinition,
  bookingBol: bookingBolDefinition,
  releaseNo: releaseNumberDefinition,
  pickupTime: pickupTimeDefinition,
  available: availabilityDefinition,
  jobDate: jobDateDefinition,
  lastFreeDay: lastFreeDayDefinition,
  trcCntrAvail: traceContainerAvailabilityDefinition,
  trcCntrHolds: traceContainerHoldsDefinition,
  trcCntrStatus: traceContainerStatusDefinition,
  trcDestInfo: traceDestinationInfoDefinition,
  trcEvent: traceEventDefinition,
  trcEventState: traceEventStateDefinition,
  trcInvStatus: traceInvoiceStatusDefinition,
  trcLFD: traceLastFreeDateDefinition,
  trcResult: traceResultDefinition,
  trcETADate: traceEtaDateDefinition
};

/** Application Default columns always to be included. Checkbox is the selection column for the Data Grid. */
export const defaultDispatchColumns = [
  dispatchColumnDefinitions.__check__,
  dispatchColumnDefinitions.actionMenu,
  dispatchColumnDefinitions.orderNumber
];

/**
 * Method for retrieving GridColDef collection used for rendering a Data Grid based on DispatchLeg entity
 */
export function getDispatchGridColDefs(boardQuery: BoardQuery) {
  const {
    configuration: {
      columns: colFieldNames,
      pinnedCols: { left: pinnedColFieldNames }
    }
  } = boardQuery;

  const actionMenuField = dispatchColumnDefinitions.actionMenu.field;
  const lastPinnedColName = pinnedColFieldNames?.at(-1);
  const seenFields = new Set<string>();
  const columns: GridColDef<DispatchLeg>[] = [];

  [...colFieldNames, ...defaultDispatchColumns.map((defaultColumn) => defaultColumn.field)].forEach((column, index) => {
    const validColumn = getEnumEntryByString(DispatchLegColumns, column);
    if (!validColumn) {
      console.log(`Invalid column not found: ${column}`);
      return;
    }

    const baseColDetails = getObjectEntryByKey(dispatchColumnDefinitions, validColumn);
    if (!baseColDetails) {
      console.log(`Invalid column not found: ${column}`);
      return;
    }

    if (seenFields.has(baseColDetails.field)) {
      console.log(`Column was already added to the collection: ${baseColDetails.field}`);
      return;
    }
    seenFields.add(baseColDetails.field);

    // Matched on the definition's field because that is what the grid pins on — a column's key is often nothing like it
    const isPinned = Boolean(
      pinnedColFieldNames?.some((pinnedColumn) => pinnedColumn.localeCompare(baseColDetails.field, 'en', { sensitivity: 'base' }) === 0)
    );
    const isLastPinned = Boolean(
      isPinned && lastPinnedColName && baseColDetails.field.localeCompare(lastPinnedColName, 'en', { sensitivity: 'base' }) === 0
    );
    const cellIndex = columns.length;
    const isAlternateCell = cellIndex % 2 === 1;
    const isActionMenu = baseColDetails.field === actionMenuField;
    const isEditable = 'renderEditableCell' in baseColDetails && Boolean(baseColDetails.renderEditableCell);

    let headerClassName: string;
    if (isPinned) {
      headerClassName = `${styles['pinned-column']} ${isLastPinned ? styles['last-pinned-col'] : ''}`;
    } else if (index % 2) {
      headerClassName = '';
    } else {
      headerClassName = styles['alternate-column'];
    }

    // Base classes precomputed once per column \u2014 only the priority class depends on the row
    const baseCellClass =
      stylesCtx({
        ['pinned-column']: isPinned,
        ['last-pinned-col']: isLastPinned,
        ['alternate-column']: isAlternateCell,
        ['row-action-cell']: isActionMenu,
        ['cell']: true,
        ['ellipsis']: true
      }) + (isEditable ? ' editable-cursor' : '');

    const cellClassName = (params: GridCellParams<DispatchLeg, unknown, unknown>) => {
      const priorityClass = styles[`priority-${String(params.row.legPriority)}`];
      return priorityClass ? `${baseCellClass} ${priorityClass}` : baseCellClass;
    };

    columns.push({
      ...baseColDetails,
      headerClassName,
      cellClassName
    });
  });

  return columns;
}

export function caseInsensitiveIncludes<T>(includesValue: string, obj: T | T[]): boolean {
  switch (typeof obj) {
    case 'object': {
      if (!obj) return false;
      const obj2 = obj as Record<string, unknown>;
      return Object.keys(obj2).some((key) => caseInsensitiveIncludes(includesValue, obj2[key]));
    }

    case 'string':
      return obj.toLowerCase().includes(includesValue.toLowerCase());

    case 'number':
      return obj.toString().includes(includesValue);

    case 'undefined':
      return false;

    default:
      throw new Error(`Unsupported type ${typeof obj} could not be processed in caseInsensitiveIncludes method.`);
  }
}

export function applyColumnQuickFilter<T>(searchValue: string, row: T, quickFilterFn?: (value: string, row: T) => boolean) {
  if (quickFilterFn) {
    return quickFilterFn(searchValue, row);
  } else {
    return false;
    //return caseInsensitiveIncludes(searchValue, row);
  }
}
